import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { deletePripustSamica } from "#/redux/resource/matingLogs/matingLogFemaleSlice";
import { getErrorMessage } from "#/utils/APIUtils.js";

const basePath = "/api/dog/pripusti";

const unwrapOutput = (data) =>
  data && typeof data === "object" && data.output ? data.output : data;
const toDateTime00 = (d) => (d ? `${d}T00:00:00` : null);
const fromDateTime = (d) => (d ? String(d).substring(0, 10) : "");
const locId = (loc) => (loc && typeof loc === "object" ? loc.id : loc);

function formToDto(form) {
  const event = form?.event || {};

  const start = event.datumZacetkaPripusta || null;
  const end = event.datumKoncaPripusta || null;

  const males = event.pripustSamci || [];
  const females = event.pripustSamice || [];

  return {
    version: event?.version ?? form?.version ?? 1,
    id: event?.id,
    tipPripusta: event.tipPripusta,
    datumPopisa: toDateTime00(event.datumPopisa),
    datumPrejema: toDateTime00(event.datumPrejema),
    datumZacetkaPripusta: toDateTime00(start),
    datumKoncaPripusta: toDateTime00(end),
    stevilkaPripustnegaLista: event.stevilkaPripustnegaLista,

    speciesSifra: event.speciesSifra || null,
    kmgMid: event.kmgMid || form.kmgMid, // keep compat
    izvajalec: event.izvajalec,
    opomba: event.opomba,

    imePredloge: (form.saveAsTemplate ? event.imePredloge : null) ?? null,
    lokacijaPripusta: locId(event.lokacijaPripusta),

    pripustSamci: males.map((m) => {
      const samecId = m.samecId ?? m.samec?.id; // your UI stores animal.id
      const recId = m?.id; // record id for PUT
      const fallbackArrival =
        form?.animalByIdMap?.[samecId]?.datumPrihoda || start;
      const fallbackDeparture =
        form?.animalByIdMap?.[samecId]?.datumOdhoda || end;
      const pripustId = m.pripustId ?? event.id;
      return {
        id: recId,
        samecId,
        pripustId,
        kondicijaSamca: m.kondicijaSamca,
        obnasanjeZivali: m.obnasanjeZivali,
        datumPrihoda: toDateTime00(m.datumPrihoda || fallbackArrival),
        datumOdhoda: toDateTime00(m.datumOdhoda || fallbackDeparture),
        version: m.version,
      };
    }),

    pripustSamice: females.map((f) => {
      const samicaId = f.samicaId || f.samica?.id || f.id;
      const recId = f.id;

      return {
        ...f,
        id: recId,
        samicaId,
        obnasanjeZivali: f.obnasanjeZivali || null,
        kondicijaSamice: f.kondicijaSamice || null,
        izidPripusta: f.izidPripusta || null,
        opombe: f.opombe || null,
        zapStPripusta: f.zapStPripusta || 0,
        zapStRojstev: f.zapStRojstev || 0,
        datumPrihoda: toDateTime00(f.datumPrihoda || start),
        datumOdhoda: toDateTime00(f.datumOdhoda || end),
      };
    }),
  };
}

// Convert Pripust DTO → your form shape (pripustSamci/pripustSamice + tipPripusta)
export function dtoToForm(dtoRaw) {
  const dto = unwrapOutput(dtoRaw);
  const lokacija = dto.lokacijaPripusta;

  return {
    kmgMid: dto.kmgMid || dto?.lokacijaPripusta?.subjekt?.id || null,
    lokacijaPripusta: lokacija,
    event: {
      ...dto,
      id: dto.id,
      kmgMid: dto.kmgMid || dto?.lokacijaPripusta?.subjekt?.id || null,
      datumPopisa: fromDateTime(dto.datumPopisa),
      speciesSifra: dto.speciesSifra || null,
      tipPripusta: dto.tipPripusta || null,
      izvajalec: dto.izvajalec || null,
      stevilkaPripustnegaLista: dto.stevilkaPripustnegaLista || null,

      // lokacijaPripusta: locId(lokacija),
      lokacijaPripusta: lokacija,
      subjekt: lokacija,
      datumZacetkaPripusta: fromDateTime(dto.datumZacetkaPripusta),
      datumKoncaPripusta: fromDateTime(dto.datumKoncaPripusta),
      opomba: dto.opomba,
      imePredloge: dto.imePredloge,
      saveAsTemplate: Boolean(dto.imePredloge),
      // IMPORTANT: UI uses animal.id for selection, but we also keep record id
      pripustSamci: (dto.pripustSamci || []).map((pripustSamca) => ({
        ...pripustSamca,
        samec: pripustSamca.samec, // keep full animal for UI (response has it)
        id: pripustSamca.id,
        samecId: pripustSamca.samecId || pripustSamca.samec?.id,
        pripustId: pripustSamca.pripustId ?? dto.id, // record id
        datumPrihoda: fromDateTime(
          pripustSamca.datumPrihoda
            ? pripustSamca.datumPrihoda
            : dto.datumZacetkaPripusta
        ),
        datumOdhoda: fromDateTime(
          pripustSamca.datumOdhoda
            ? pripustSamca.datumOdhoda
            : dto.datumKoncaPripusta
        ),
        obnasanjeZivali: pripustSamca.obnasanjeZivali || null,
        kondicijaSamca: pripustSamca.kondicijaSamca || null,
      })),

      pripustSamice: (dto.pripustSamice || []).map((pripustSamice) => ({
        ...pripustSamice,
        samica: pripustSamice.samica,
        id: pripustSamice.samicaId || pripustSamice.samica?.id,
        samicaId: pripustSamice.samicaId || pripustSamice.samica?.id,
        pripustSamicaId: pripustSamice.id,
        datumPrihoda: fromDateTime(
          pripustSamice.datumPrihoda
            ? pripustSamice.datumPrihoda
            : dto.datumZacetkaPripusta
        ),
        datumOdhoda: fromDateTime(
          pripustSamice.datumOdhoda
            ? pripustSamice.datumOdhoda
            : dto.datumKoncaPripusta
        ),
        obnasanjeZivali: pripustSamice.obnasanjeZivali,
        kondicijaSamice: pripustSamice.kondicijaSamice,
        izidPripusta: pripustSamice.izidPripusta,
        opombe: pripustSamice.opombe,
        zapStPripusta: pripustSamice.zapStPripusta,
        zapStRojstev: pripustSamice.zapStRojstev,
      })),
    },
    opomba: dto.opomba,
    imePredloge: dto.imePredloge,
    saveAsTemplate: Boolean(dto.imePredloge),
  };
}

// Compute removed male/female record IDs
function computeDeleted(existing, current) {
  const existingMap = new Map(existing.map((e) => [e.id, e]));
  const currentIds = new Set(current.map((e) => e.id));

  const deleted = [];

  for (const [recId, obj] of existingMap.entries()) {
    if (!currentIds.has(obj.id)) {
      deleted.push(obj);
    }
  }
  return deleted;
}

export const createMatingLog = createAsyncThunk(
  "matingLog/create",
  async (formValues, thunkAPI) => {
    try {
      const dto = formToDto(formValues);
      const response = await ApiClient.post(`${basePath}/pripust`, dto);
      return dtoToForm(unwrapOutput(response.data));
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

export const createMatingLogFromDto = createAsyncThunk(
  "matingLog/create/fromDto",
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`${basePath}/pripust`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.details ?? getErrorMessage(error)
      );
    }
  }
);

export const getMatingLogById = createAsyncThunk(
  "matingLog/getById",
  async (id, thunkAPI) => {
    try {
      const response = await ApiClient.get(`${basePath}/pripust/${id}`);
      return unwrapOutput(response.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

export const deleteMaleFromPripust = createAsyncThunk(
  "matingLog/deleteMale",
  async ({ maleRecordId, version }, thunkAPI) => {
    try {
      await ApiClient.delete(
        `${basePath}/pripustSamci/${maleRecordId}${version == null ? "" : `?version=${version}`}`
      );
      return maleRecordId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

export const deleteFemaleFromPripust = createAsyncThunk(
  "matingLog/deleteFemale",
  async (femaleRecordId, thunkAPI) => {
    try {
      await ApiClient.delete(`${basePath}/pripustSamica/${femaleRecordId}`);
      return femaleRecordId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

export const updateMatingLogFromDto = createAsyncThunk(
  "matingLog/update/fromDto",
  async ({ dto, id }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState()?.matingLogs;
      const existingDTO = state?.byId?.[id];

      const deletedMales = computeDeleted(
        existingDTO?.pripustSamci || [],
        dto.pripustSamci || []
      );
      const deletedFemales = computeDeleted(
        existingDTO?.pripustSamice || [],
        dto.pripustSamice || []
      );

      await Promise.all([
        ...deletedMales.map((m) =>
          dispatch(
            deleteMaleFromPripust({ maleRecordId: m.id, version: m.version })
          )
        ),
        ...deletedFemales.map((f) => dispatch(deletePripustSamica(f))),
      ]);

      const response = await ApiClient.put(`${basePath}/pripust/${id}`, {
        ...dto,
        pripustSamice: (dto.pripustSamice ?? []).filter((ps) => ps.id == null),
        pripustSamci: (dto.pripustSamci ?? []).filter((ps) => ps.id == null),
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const updateMatingLog = createAsyncThunk(
  "matingLog/update",
  async (formValues, thunkAPI) => {
    try {
      const currentDTO = formToDto(formValues);
      const { id } = currentDTO;
      if (!id) throw new Error("Missing pripust.id for update");

      const state = thunkAPI.getState()?.matingLogs;
      const existingDTO = state?.byId?.[id];

      const deletedMales = computeDeleted(
        existingDTO?.pripustSamci || [],
        currentDTO.pripustSamci || []
      );
      const deletedFemales = computeDeleted(
        existingDTO?.pripustSamice || [],
        currentDTO.pripustSamice || []
      );

      await Promise.all([
        ...deletedMales.map((m) =>
          thunkAPI.dispatch(deleteMaleFromPripust(m.id))
        ),
        ...deletedFemales.map((f) => thunkAPI.dispatch(deletePripustSamica(f))),
      ]);

      const response = await ApiClient.put(
        `${basePath}/pripust/${id}`,
        currentDTO
      );
      return unwrapOutput(response.data);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

export const findMatingLogs = createAsyncThunk(
  "matingLog/find",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${basePath}/pripust/page`,
        null,
        page,
        filter,
        sort,
        false
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export const fetchMatingLogTemplatePage = createAsyncThunk(
  "matingLog/templates",
  async ({ page, filter, sort }, thunkAPI) => {
    try {
      const response = await ApiClient.get(
        `${basePath}/pripust/page`,
        null,
        page,
        // new ApiPage().fromJson(page).toQueryString()
        filter,
        // new ApiSort().fromJson(sort).toQueryString(),
        sort,
        true
      );
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err);
    }
  }
);

const matingLogSlice = createSlice({
  name: "matingLog",
  initialState: {
    list: { content: [], isLoading: false, error: null },
    templatePage: { content: [], totalElements: 0, isLoading: false },
    byId: {},
    page: {},
    isLoading: false,
    error: null,
  },

  reducers: {
    resetMatingLogPageState: (state) => {
      state.page = {};
    },
    resetMatingLogAssessment: (state) => {
      state.details = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(createMatingLog.fulfilled, (state, action) => {
        const dto = action.payload;
        state.byId[dto.id] = dto;
      })
      .addCase(createMatingLogFromDto.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(createMatingLogFromDto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createMatingLogFromDto.pending, (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMatingLogFromDto.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(updateMatingLogFromDto.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateMatingLogFromDto.pending, (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMatingLogById.fulfilled, (state, action) => {
        const dto = action.payload;
        state.byId[dto.id] = dto;
      })

      .addCase(updateMatingLog.fulfilled, (state, action) => {
        const dto = action.payload;
        state.byId[dto.id] = dto;
      })

      .addCase(findMatingLogs.pending, (state) => {
        state.list.isLoading = true;
      })
      .addCase(findMatingLogs.fulfilled, (state, action) => {
        state.list = {
          content: action.payload.content ?? [],
          totalElements: action.payload.totalElements ?? 0,
          isLoading: false,
        };
        state.page = action.payload;
      })
      .addCase(findMatingLogs.rejected, (state) => {
        state.list.isLoading = false;
      })

      .addCase(fetchMatingLogTemplatePage.pending, (state) => {
        state.templatePage.isLoading = true;
      })
      .addCase(fetchMatingLogTemplatePage.fulfilled, (state, action) => {
        state.templatePage = {
          content: action.payload.content || [],
          totalElements: action.payload.totalElements || 0,
          isLoading: false,
        };
      })
      .addCase(fetchMatingLogTemplatePage.rejected, (state) => {
        state.templatePage.isLoading = false;
      });
  },
});

export const { resetMatingLogPageState, resetMatingLogAssessment } =
  matingLogSlice.actions;

export default matingLogSlice.reducer;
